import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { readDeviceHelp, deviceHelpIssues, deviceHelpSummary } from '../device-help.js';

// /app/device-help: where a display's SETUP > HELP code lands (Trident OLED
// 1.0.6+), and where the link page sends someone who is stuck. It explains the
// display's own state in plain words and has a "write to us" form that works
// signed out (POST /api/feedback, screen 'device-help', which is never merged
// into another ticket). Phone first: one column, 44 px targets.

function SettingsSteps({ help, children }) {
  return (
    <ol className="device-help-steps">
      <li>
        Put your phone on the same Wi-Fi as the display.{' '}
        {help.settingsUrl ? (
          <>
            Then open{' '}
            <a href={help.settingsUrl} rel="noreferrer">
              {help.settingsUrl.replace(/\/$/, '')}
            </a>
            .
          </>
        ) : (
          'Then open the address the display shows on SETTINGS > NETWORK, at the bottom.'
        )}
      </li>
      <li>
        Your phone asks for a name and password. Any name works; the password is the PIN at the
        bottom of the display&rsquo;s SETUP page (swipe down, tap SETUP).
      </li>
      {children}
      <li>Tap Save &amp; Connect. The display restarts and tries again.</li>
    </ol>
  );
}

function Issue({ issue, help }) {
  switch (issue.key) {
    case 'wifi':
      return (
        <section className="device-help-issue">
          <h2>
            {issue.problem === 'password'
              ? 'The display cannot join your Wi-Fi: the password may be wrong'
              : issue.problem === 'not_found'
                ? 'The display cannot find your Wi-Fi network'
                : 'The display cannot join your Wi-Fi'}
          </h2>
          <ol className="device-help-steps">
            <li>
              On the display, swipe down, tap SETUP, then WI-FI, and tap it again to confirm. The
              display restarts and shows a code.
            </li>
            <li>
              Scan that code with your phone&rsquo;s camera to join the display&rsquo;s setup
              network.
            </li>
            <li>
              Pick your home Wi-Fi and type its password (tick Show password to check it), then Save
              &amp; Connect.
            </li>
          </ol>
          <p className="ts-hint">
            The display uses 2.4 GHz Wi-Fi only. If your router gives its 2.4 GHz and 5 GHz networks
            separate names, pick the 2.4 GHz one. To leave setup without changing anything, unplug
            the display and plug it back in.
          </p>
        </section>
      );
    case 'apex_not_found':
      return (
        <section className="device-help-issue">
          <h2>The display has not found your Apex</h2>
          <p>
            The display looks for the Apex on its own Wi-Fi network, so both must be on the same
            network: not a guest network, which keeps devices apart. If you also changed the
            Apex&rsquo;s login from admin / 1234, the display may not find it by itself.
          </p>
          <SettingsSteps help={help}>
            <li>
              Under APEX, type the Apex&rsquo;s address in Host or IP, and the user name and
              password you sign in to the Apex with. Your router&rsquo;s list of connected devices
              shows the Apex&rsquo;s address (a number like 192.168.1.50).
            </li>
          </SettingsSteps>
        </section>
      );
    case 'apex_searching':
      return (
        <section className="device-help-issue">
          <h2>The display is still looking for your Apex</h2>
          <p>
            Give it a few minutes. If it then says APEX NOT FOUND, open HELP on the display again
            for the next steps.
          </p>
        </section>
      );
    case 'apex_login':
      return (
        <section className="device-help-issue">
          <h2>The Apex turned down the display&rsquo;s login</h2>
          <p>The Apex&rsquo;s login was changed from admin / 1234, so the display needs yours.</p>
          <SettingsSteps help={help}>
            <li>Under APEX, type the user name and password you sign in to the Apex with.</li>
          </SettingsSteps>
        </section>
      );
    case 'apex_unreachable':
      return (
        <section className="device-help-issue">
          <h2>The Apex is not answering</h2>
          <p>
            Check that the Apex is on and on the same Wi-Fi network. Its address can change after
            the router restarts; if it did, type the new one.
          </p>
          <SettingsSteps help={help}>
            <li>Under APEX, tap Host or IP to pick your Apex, or type its new address.</li>
          </SettingsSteps>
        </section>
      );
    case 'apex_many':
      return (
        <section className="device-help-issue">
          <h2>The display found more than one Apex</h2>
          <p>It took the first one it found. If that is not the one with your Trident:</p>
          <SettingsSteps help={help}>
            <li>Under APEX, tap Host or IP and pick yours.</li>
          </SettingsSteps>
        </section>
      );
    case 'cloud':
      return (
        <section className="device-help-issue">
          <h2>The display is not connected to TankSensei yet</h2>
          <p>
            It connects by itself once it is on Wi-Fi; the first time can take a few minutes. Your
            Apex readings still show on the display meanwhile. If it stays like this for an hour,
            write to us below.
          </p>
        </section>
      );
    case 'not_linked':
      return (
        <section className="device-help-issue">
          <h2>Add the display to TankSensei</h2>
          <ol className="device-help-steps">
            <li>On the display, swipe down, tap SETUP, then LINK.</li>
            <li>
              Scan the code with your phone&rsquo;s camera. Sign in with your email, or make an
              account (free during beta), and pick the tank.
            </li>
          </ol>
          <p className="ts-hint">
            Linking finishes once the display shows your Apex&rsquo;s numbers.
          </p>
        </section>
      );
    default:
      return null;
  }
}

export function DeviceHelpPage({ user }) {
  const help = readDeviceHelp(typeof window === 'undefined' ? '' : window.location.search);
  const issues = deviceHelpIssues(help);
  const summary = deviceHelpSummary(help);

  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  // "#write-to-us" from the link page: the page renders after load, so the
  // browser's own jump to the anchor finds nothing.
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#write-to-us') return;
    document.getElementById('write-to-us')?.scrollIntoView();
  }, []);

  const send = async () => {
    if (message.trim().length < 3) {
      setErr('Tell us what happened.');
      return;
    }
    if (!user && !email.trim()) {
      setErr('Add your email so we can answer you.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/feedback', {
        method: 'POST',
        body: {
          message: summary ? `${message.trim()}\n\n--\n${summary}` : message.trim(),
          email: email.trim() || undefined,
          screen: 'device-help',
          kind: 'question',
        },
      });
      setSent(true);
    } catch (e) {
      setErr(e.message || 'Could not send. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="claim-page device-help" style={shellStyle}>
      <h1 className="font-heading">Display help</h1>
      {help.kind === 'trident' ? (
        issues.length ? (
          <p>Here is what your Trident OLED told us, and what to do.</p>
        ) : (
          <p>Your Trident OLED looks fine: on Wi-Fi, reading your Apex and linked to TankSensei.</p>
        )
      ) : (
        <>
          <p>
            For the Trident OLED: swipe down on the display, tap SETUP, then HELP, and scan the code
            with your phone&rsquo;s camera. This page then shows what the display reports and what
            to do.
          </p>
          <p>Or tell us what is happening below and we&rsquo;ll help.</p>
        </>
      )}

      {issues.map((issue) => (
        <Issue key={issue.key} issue={issue} help={help} />
      ))}

      <section className="device-help-issue" id="write-to-us">
        <h2>Still stuck? Write to us</h2>
        {sent ? (
          <p role="status">
            Sent. We&rsquo;ll answer{email.trim() ? ` at ${email.trim()}` : ' by email'}.
          </p>
        ) : (
          <>
            {!user && (
              <label className="ts-field">
                <span>Your email</span>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            )}
            <label className="ts-field" style={{ marginTop: 10 }}>
              <span>What happened?</span>
              <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
            </label>
            {summary && (
              <p className="ts-hint" style={{ whiteSpace: 'pre-line' }}>
                {'We add what the display reported:\n'}
                {summary}
              </p>
            )}
            {err && (
              <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
                {err}
              </div>
            )}
            <button
              type="button"
              className="ts-save"
              style={{ marginTop: 10, minHeight: 44 }}
              onClick={send}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
          </>
        )}
      </section>

      <p style={{ marginTop: 20 }}>
        <a href="/app/">Open TankSensei</a>
      </p>
    </main>
  );
}

const shellStyle = {
  maxWidth: 520,
  margin: '0 auto',
  padding: '32px 20px 60px',
};
